<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Games from "@/services/Games";
import Swal from "sweetalert2";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import modalTranslateQuestion from "@/components/modals/games/modalTranslateQuestion";

/**
 * Quiz - Question Details component
 */
export default {
  components: {
    Layout,
    PageHeader,
    vueDropzone: vue2Dropzone,
    modalTranslateQuestion
  },
  computed: {
    reviewStats() {
      const total = Array.isArray(this.reviewedQuestions) ? this.reviewedQuestions.length : 0;
      const numCorrect = total
        ? this.reviewedQuestions.filter(r => r && r.is_correct === 1).length
        : 0;
      const numIncorrect = total
        ? this.reviewedQuestions.filter(r => r && r.is_correct === 0).length
        : 0;
      const percentCorrect = total ? Math.round((numCorrect / total) * 100) : 0;
      return { total, numCorrect, numIncorrect, percentCorrect };
    },
    reviewRecommendation() {
      // Approve if all correct or >= 50% correct, else reject
      if (this.reviewStats.total === 0) return null;
      return this.reviewStats.percentCorrect >= 50 ? 'approve' : 'reject';
    },
    reviewAlertClass() {
      if (!this.reviewRecommendation) return 'alert-info';
      return this.reviewRecommendation === 'approve' ? 'alert-success' : 'alert-danger';
    },
    // Finalized state (already approved or rejected)
    isFinalized() {
      if (!this.questionDetails) return false;
      return this.questionDetails.is_approved === 1 || this.questionDetails.is_rejected === 1;
    },
    finalStatusText() {
      if (!this.isFinalized) return '';
      if (this.questionDetails.is_rejected === 1) return 'This question has been rejected.';
      if (this.questionDetails.is_approved === 1) return 'This question has been approved.';
      return '';
    },
    currentReviewAlertClass() {
      // If finalized, lock the alert color to status
      if (this.isFinalized) {
        return this.questionDetails.is_rejected === 1 ? 'alert-danger' : 'alert-success';
      }
      return this.reviewAlertClass;
    },
    reviewActionLabel() {
      if (!this.reviewRecommendation) return '';
      return this.reviewRecommendation === 'approve' ? 'Approve' : 'Reject';
    }
  },
  page: {
    title: "Quiz - Question Details",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "",
      items: [
        {
          text: "Quiz",
          href: "/games/quiz",
        },
        {
          text: "Question Details",
          active: true,
        },
      ],
      questionDetails: { answers: [], translations: [] },
      reviewedImages: [],
      reviewedQuestions: [],
      showImageDropzone: false,
      tryingToEdit: false,
      image_url: null,
      selectedCorrectAnswerId: null,
      error: null,
      errorQuestionDetails: null,
      toggleProfile: false,
      submitted: false,
      uploading: false,
      showLoader: false,
      showLoaderImages: false,
      showLoaderReviewedQuestions: false,
      loadingStates: {
        approving: false,
        rejecting: false
      },
      dropzoneOptions_primary: {
        url: '/',
        autoProcessQueue: false,
        addRemoveLinks: true,
        thumbnailWidth: 200,
        thumbnailMethod: 'contain',
        maxFilesize: 2,
        acceptedFiles: 'image/jpeg,image/png,image/gif,image/bmp,image/webp',
        maxFiles: 1,
      },
    };
  },
  async mounted() {
    this.getQuestionDetails()
    this.getReviewedImages()
    this.getReviewedQuestions()
  },
  methods: {

    getQuestionDetails() {
      this.showLoader = true

      const query = `id=${encodeURIComponent(this.$route.params.id)}&details=1`;
      Games.getQuizQuestion(query).then((response) => {
        const records = response.data.data;
        if (Array.isArray(records) && records.length) {
          this.questionDetails = records[0];
          // Ensure arrays exist
          this.questionDetails.answers = this.questionDetails.answers || [];
          this.questionDetails.translations = this.questionDetails.translations || [];

          const correct = this.questionDetails.answers.find(
              (a) => a.correct_answer === 1
          );
          if (correct) {
            this.selectedCorrectAnswerId = correct.id;
          }

        } else {
          this.questionDetails = { answers: [], translations: [] };
        }
      }).catch((error) => {
        this.errorquestionDetails = error.response.data.error
            ? error.response.data.error
            : "";
      }).finally(() => {
        this.showLoader = false
      });
    },

    toggle() {
      this.toggleProfile = !this.toggleProfile;
    },

    async handleCorrectAnswerChange(id, event) {
      // Prevent immediate radio button change
      event.preventDefault();

      const result = await Swal.fire({
        title: 'Change Correct Answer?',
        text: 'Are you sure you want to change the correct answer?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: 'Yes, change it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        // Only update the model if confirmed
        this.selectedCorrectAnswerId = id;
        this.questionDetails.answers.forEach(ans => {
          ans.correct_answer = ans.id === id;
        });

        const answerParams = {
          answer_id: id,
          correct_answer: 1,
          type: 'correctAnswer'
        };

        this.editQuizAnswer(answerParams);
      }
      // If cancelled, do nothing - radio button won't change
    },

    editQuizAnswer(answerParams) {
      let data = {};

      if (answerParams.type === 'correctAnswer') {
        data = {
          question_id: this.questionDetails.id,
          answer_id: answerParams.answer_id,
          correct_answer: answerParams.correct_answer,
        };
      } else if (answerParams.type === 'editAnswer') {
        data = {
          question_id: this.questionDetails.id,
          answer_id: answerParams.answer_id,
          language: answerParams.language,
          answer: answerParams.answer,
        };
      }

      Games.editQuizAnswer(data)
          .then((response) => {
            if (answerParams.type === 'correctAnswer') {
              this.successmsg("Successful changed correct answer");
            }

            if (answerParams.type === 'editAnswer') {
              this.successmsg("Successful changed answer value");
            }
          })
          .catch((error) => {
            let errorMessage = "An unexpected error occurred";

            if (error.response && error.response.data && error.response.data.error) {
              if (Array.isArray(error.response.data.error)) {
                errorMessage = error.response.data.error.join(", ");
              } else {
                errorMessage = error.response.data.error;
              }
            } else if (error.message) {
              errorMessage = error.message;
            } else if (error.request) {
              errorMessage = "No response received from server";
            }

            this.failedmsg(errorMessage);
            this.getQuestionDetails()
            console.error("Quiz Answer error:", error);
          });
    },


    async toggleApproveQuestion() {
      if (this.loadingStates.approving) return; // Prevent multiple clicks

      this.loadingStates.approving = true;

      try {
        // Toggle approval status only
        const newApprovalStatus = this.questionDetails.is_approved ? 0 : 1;

        // Send only approval status, keep rejection status unchanged
        await this.editQuizQuestion({ is_approved: newApprovalStatus });

        // Update local state AFTER successful response
        this.questionDetails.is_approved = newApprovalStatus;

        Swal.fire({
          icon: 'success',
          title: newApprovalStatus ? 'Question Approved!' : 'Question Unapproved!',
          text: newApprovalStatus
              ? 'The question has been successfully approved and is now active.'
              : 'The question has been unapproved and is no longer active.',
          timer: 3000,
          showConfirmButton: false,
        });

      } catch (error) {
        // Reload original data on error
        this.getQuestionDetails();
        this.handleError(error, "approval");
      } finally {
        this.loadingStates.approving = false;
      }
    },

    async toggleRejectQuestion() {
      if (this.loadingStates.rejecting) return;

      this.loadingStates.rejecting = true;

      try {
        // Toggle rejection status only
        const newRejectionStatus = this.questionDetails.is_rejected ? 0 : 1;

        // Send only rejection status, keep approval status unchanged
        await this.editQuizQuestion({ is_rejected: newRejectionStatus });

        // Update local state AFTER successful response
        this.questionDetails.is_rejected = newRejectionStatus;

        Swal.fire({
          icon: 'success',
          title: newRejectionStatus ? 'Question Rejected!' : 'Question Unrejected!',
          text: newRejectionStatus
              ? 'The question has been successfully rejected and will not be shown.'
              : 'The question rejection has been removed.',
          timer: 3000,
          showConfirmButton: false,
        });

      } catch (error) {
        // Reload original data on error
        this.getQuestionDetails();
        this.handleError(error, "rejection");
      } finally {
        this.loadingStates.rejecting = false;
      }
    },

    async editQuizQuestion(statusOverride = {}){
      this.tryingToEdit = true;
      this.submitted = true;
      this.uploading = true;

      try {
        // Prepare data according to API requirements
        const formData = new FormData();

        // Core question fields
        formData.append('question_id', this.questionDetails.id);
        formData.append('team_id', this.questionDetails.team_id);
        formData.append('question', this.questionDetails.question);
        formData.append('difficulty', this.questionDetails.difficulty);
        formData.append('ai_model', this.questionDetails.ai_model);
        formData.append('ai_log_id', this.questionDetails.ai_log_id || '');
        const approvedToSend = Object.prototype.hasOwnProperty.call(statusOverride, 'is_approved')
          ? statusOverride.is_approved
          : (this.questionDetails.is_approved ?? '');
        const rejectedToSend = Object.prototype.hasOwnProperty.call(statusOverride, 'is_rejected')
          ? statusOverride.is_rejected
          : (this.questionDetails.is_rejected ?? '');

        formData.append('is_approved', approvedToSend);
        formData.append('is_rejected', rejectedToSend);

        // Add CSRF and method ONCE, outside the loop
        formData.append('csrf_token', this.csrf_token);
        formData.append('_method', 'PATCH');

        // If there's an image to update
        if (this.image_url instanceof File) {
          formData.append('image', this.image_url);
        }
        /*if (this.questionDetails.image && typeof this.questionDetails.image !== 'string') {

        }*/

        // Add translations (flattened for backend)
        this.questionDetails.translations.forEach((t, index) => {
          formData.append(`translations[${index}][language]`, t.language);
          formData.append(`translations[${index}][question]`, t.question);
        });

        // Add answers
        this.questionDetails.answers.forEach((a, i) => {
          formData.append(`answers[${i}][id]`, a.id);
          formData.append(`answers[${i}][answer]`, a.answer);
          formData.append(`answers[${i}][correct_answer]`, a.correct_answer);
          formData.append(`answers[${i}][question_id]`, a.question_id);

          // Add nested translations
          a.translations.forEach((t, j) => {
            formData.append(`answers[${i}][translations][${j}][language]`, t.language);
            formData.append(`answers[${i}][translations][${j}][answer]`, t.answer);
          });
        });

        await Games.editQuizQuestion(this.$route.params.id, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then((response) => {
          this.successmsg("Quiz question has been saved successfully");
          this.getQuestionDetails()
        }).catch(error => {
          let errorMessage = "An unexpected error occurred";

          if (error.response && error.response.data && error.response.data.error) {
            // Handle array of errors from API
            if (Array.isArray(error.response.data.error)) {
              errorMessage = error.response.data.error.join(', ');
            } else {
              errorMessage = error.response.data.error;
            }
          } else if (error.message) {
            errorMessage = error.message;
          } else if (error.request) {
            errorMessage = "No response received from server";
          }

          this.failedmsg(errorMessage);
          console.error("Quiz question error:", error);
        }).finally(() => {
          this.tryingToEdit = false
          this.cancelImageUpload()
        });
      } catch (error) {
        let errorMessage = "An unexpected error occurred";

        if (error.response && error.response.data && error.response.data.error) {
          if (Array.isArray(error.response.data.error)) {
            errorMessage = error.response.data.error.join(', ');
          } else {
            errorMessage = error.response.data.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.failedmsg(errorMessage);
        console.error("Quiz question error:", error);
        this.tryingToEdit = false;
        this.uploading = false;
      }
    },
    async addImageToReview() {
      this.tryingToEdit = true;
      this.submitted = true;
      this.uploading = true;

      try {
        // Validation
        if (!this.questionDetails?.id) throw new Error('Question ID required');
        if (!localStorage.getItem('username')) throw new Error('User not authenticated');

        // Prepare form data
        const formData = new FormData();
        formData.append('question_id', this.$route.params.id);
        formData.append('reviewer_name', localStorage.getItem('username'));
        formData.append('reviewer_type', 'human');
        formData.append('csrf_token', this.csrf_token);
        if (this.image_url instanceof File) formData.append('image', this.image_url);

        // API call
        await Games.addImageToReview(formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        this.successmsg("Image uploaded successfully");
        await this.getQuestionDetails();
        await this.getReviewedImages();

      } catch (error) {
        const errorMessage = error?.response?.data?.error
            ? (Array.isArray(error.response.data.error)
                ? error.response.data.error.join(', ')
                : error.response.data.error)
            : error?.message || "An unexpected error occurred";

        this.failedmsg(errorMessage);
        console.error("Quiz question error:", error);
      } finally {
        this.tryingToEdit = false;
        this.uploading = false;
        this.cancelImageUpload();
      }
    },

    async editAnswer(answer) {
      const { value: newAnswer } = await Swal.fire({
        title: 'Edit Answer',
        input: "text",
        inputValue: answer.answer,
        inputPlaceholder: "Enter answer text...",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Save",
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return "Answer cannot be empty";
          }
          return null;
        }
      });

      // Update answer if confirmed
      if (newAnswer && newAnswer.trim() !== answer.answer) {
        answer.answer = newAnswer.trim();

        const answerParams = {
          answer_id: answer.id,
          answer: answer.answer,
          language: 'en',
          type: 'editAnswer'
        };

        this.editQuizAnswer(answerParams);
      }
    },
    async editDifficulty() {
      const { value: newDifficulty } = await Swal.fire({
        title: 'Edit Difficulty',
        input: "select",
        inputOptions: {
          Difficulty: {
            easy: "Easy",
            medium: "Medium",
            hard: "Hard",
          },
        },
        inputValue: this.questionDetails.difficulty,
        inputPlaceholder: "",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Save",
        inputValidator: (value) => {
          if (!value) {
            return "Please select a difficulty";
          }
          return null;
        },
      });

      // Persist only if changed
      if (newDifficulty && newDifficulty !== this.questionDetails.difficulty) {
        this.questionDetails.difficulty = newDifficulty;
        await this.editQuizQuestion();
        this.successmsg("Difficulty updated successfully");
      }
    },

    async changeAnswerTranslation(answer_id, language, defaultAnswer) {
      const answer = this.questionDetails.answers.find(a => a.id === answer_id);
      if (!answer) return this.failedmsg("Answer not found");

      const translation = answer.translations.find(t => t.language === language);
      if (!translation) return this.failedmsg("Translation not found");

      const { value: newTranslation } = await Swal.fire({
        title: `Edit Translation (${language.toUpperCase()}) for ${defaultAnswer}`,
        input: "text",
        inputValue: translation.answer,
        inputPlaceholder: "Enter new translation...",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Save",
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return "Translation cannot be empty";
          }
          return null;
        }
      });

      // Update translation if confirmed
      if (newTranslation && newTranslation.trim() !== translation.answer) {
        translation.answer = newTranslation.trim();

        const answerParams = {
          answer_id: answer_id,
          answer: translation.answer,
          language: language,
          type: 'editAnswer'
        };

        this.editQuizAnswer(answerParams);
      }
    },

    async changeQuestionTranslation(language) {
      const question = this.questionDetails;
      if (!question) return this.failedmsg("Question not found");

      const translation = question.translations.find(t => t.language === language);
      if (!translation) return this.failedmsg("Translation not found");

      const { value: newTranslation } = await Swal.fire({
        title: `Edit Question Translation (${language.toUpperCase()})`,
        input: "textarea",
        inputValue: translation.question,
        inputPlaceholder: "Enter new translation...",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Save",
        inputAttributes: {
          'rows': 4
        },
        inputValidator: (value) => {
          if (!value.trim()) {
            return "Translation cannot be empty";
          }
        }
      });

      // Update translation if confirmed
      if (newTranslation && newTranslation.trim() !== translation.question) {
        translation.question = newTranslation.trim();

        const questionParams = {
          question_id: question.id,
          question: translation.question,
          language: language,
        };

        this.editQuizQuestionTranslation(questionParams);
      }
    },

    editQuizQuestionTranslation(questionParams){

      Games.editQuizQuestionTranslation(questionParams)
          .then((response) => {
            this.successmsg("Successful changed "+questionParams.language+" question value");
          })
          .catch((error) => {
            let errorMessage = "An unexpected error occurred";

            if (error.response && error.response.data && error.response.data.error) {
              if (Array.isArray(error.response.data.error)) {
                errorMessage = error.response.data.error.join(", ");
              } else {
                errorMessage = error.response.data.error;
              }
            } else if (error.message) {
              errorMessage = error.message;
            } else if (error.request) {
              errorMessage = "No response received from server";
            }

            this.failedmsg(errorMessage);
            this.getQuestionDetails()
            console.error("Quiz Question error:", error);
          });
    },
    cancelImageUpload() {
      this.showImageDropzone = false
      this.image_url = ''
    },

    selectFileImageUrl(files) {
      setTimeout(() => {
        let queuedFiles = this.$refs.myVueDropzone_image_url.getQueuedFiles();
        if (queuedFiles.length > 1) {
          this.failedmsg('You can only have one image');

          files.forEach((value) => {
            this.$refs.myVueDropzone_image_url.removeFile(value);
          });
        }
        this.image_url = queuedFiles[0] || null;
        this.photoDeletedLogoUrl = false;
      }, 250);
    },

    saveImageChange(){
      this.editQuizQuestion()
      this.getReviewedImages()
    },

    getAnswerTranslation(answer, language) {
      if (!answer.translations || !answer.translations.length) {
        return answer.answer; // Return default if no translations
      }
      
      const translation = answer.translations.find(t => t.language === language);
      return translation ? translation.answer : answer.answer;
    },

    openTranslateModal() {
      this.$refs.translateQuestionModal.showModal = true;
    },

    getReviewedImages() {
      this.showLoaderImages = true;
      
      Games.getReviewedImages('question_id=' + this.$route.params.id)
        .then((response) => {
          this.reviewedImages = response.data.data || [];
        })
        .catch((error) => {
          console.error('Error fetching reviewed images:', error);
          this.reviewedImages = [];
        })
        .finally(() => {
          this.showLoaderImages = false;
        });
    },

    async approveReviewedImage(review) {
      const result = await Swal.fire({
        title: 'Approve & Use This Image?',
        text: 'This will set this image as the question image. Are you sure?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: 'Yes, use it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        try {
          await Games.approveReviewedImage({
            review_id: review.id,
            question_id: this.$route.params.id
          });

          this.successmsg('Image approved and applied to question successfully');
          
          // Refresh both the question details and reviewed images
          this.getQuestionDetails();
          this.getReviewedImages();
        } catch (error) {
          let errorMessage = "An unexpected error occurred";

          if (error.response && error.response.data && error.response.data.error) {
            if (Array.isArray(error.response.data.error)) {
              errorMessage = error.response.data.error.join(', ');
            } else {
              errorMessage = error.response.data.error;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.failedmsg(errorMessage);
          console.error('Error approving image:', error);
        }
      }
    },

    isCurrentImage(reviewImageUrl) {
      // Compare the review image URL with the current question's image URL
      if (!reviewImageUrl || !this.questionDetails.image) {
        return false;
      }
      return reviewImageUrl === this.questionDetails.image;
    },

    getReviewedQuestions() {
      this.showLoaderReviewedQuestions = true;
      
      Games.getReviewedQuestions('question_id=' + this.$route.params.id)
        .then((response) => {
          this.reviewedQuestions = response.data.data || [];
        })
        .catch((error) => {
          console.error('Error fetching reviewed questions:', error);
          this.reviewedQuestions = [];
        })
        .finally(() => {
          this.showLoaderReviewedQuestions = false;
        });
    },

  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row mb-4" v-if="!this.error">
      <div v-if="!toggleProfile" class="col-xl-4">
        <div class="card h-100">
          <div class="card-body" v-if="!showLoader">
            <div class="text-center">
<!--              <b-dropdown
                  class="float-end"
                  variant="white"
                  right
                  menu-class="dropdown-menu-end"
                  toggle-class="font-size-16 text-body p-0"
              >
                <template v-slot:button-content>
                  <i class="uil uil-ellipsis-v"></i>
                </template>
                <a
                    class="dropdown-item"
                    href="javascript:void(0);"

                >Approve Question</a>
                <a
                    class="dropdown-item"
                    href="javascript:void(0);"

                >Reject Question</a>
              </b-dropdown>-->

              <i class="uil uil-refresh font-size-20 btn p-0 link-primary" @click="getQuestionDetails()" title="Refresh Data"></i>
              <div class="clearfix"></div>

              <p class="font-size-16 mt-4 mb-2 justify-content-between">Question:</p>
              <h5 class="mt-2 mb-4">{{questionDetails.question}}</h5>

              <h3 class="mt-3 mb-1" v-if="questionDetails">
                <img v-if="questionDetails.image" :src="questionDetails.image" alt="quiz question image" class="img-thumbnail w-75" />
                <img v-else src="@/assets/images/image_not_found.png" class="img-thumbnail w-50" />
              </h3>


              
            </div>


            <hr class="my-2" />

            <div class="table-responsive mt-4 mb-0">
              <h5>Quiz Question Information</h5>
              <!-- <div class="mt-3">
                   <p class="mb-2 text-strong">Question :</p>
                   <p class="font-size-16 mb-2 justify-content-between">
                       <span class="mw-80">{{questionDetails.question}}</span>
                   </p>
               </div>-->
              <div class="mt-3">
                <p class="mb-2 text-strong">Difficulty :</p>
                <p class="font-size-16 mb-2 justify-content-between">
                  <span class="mw-80">{{questionDetails.difficulty}}</span>
                  <a href="javascript:void(0);"
                     class="text-primary ml-2"
                     @click="editDifficulty(difficult)"
                     title="Edit Answer">
                    <i class="uil uil-pen font-size-18"></i>
                  </a>
                </p>
              </div>
              <div class="mt-3">
                <p class="mb-2 text-strong">Is Approved :</p>
                <p class="font-size-16 mb-2 justify-content-between">
                  <span v-if="questionDetails.is_approved" class='mw-80 badge bg-success'>Approved</span>
                  <span v-else class="mw-80 badge bg-warning">Not Approved</span>
                </p>
              </div>
              <div class="mt-3">
                <p class="mb-2 text-strong">Is Rejected :</p>
                <p class="font-size-16 mb-2 justify-content-between">
                  <span v-if="questionDetails.is_rejected" class='mw-80 badge bg-warning'>Rejected</span>
                  <span v-else class="mw-80 badge bg-success">Not Rejected</span>
                </p>
              </div>
              <div class="mt-3">
                <p class="mb-2 text-strong">For Team :</p>
                <p class="font-size-16 mb-2 justify-content-between">
                  <span class="mw-80">{{questionDetails.team_id}}</span>
                </p>
              </div>
              <div class="mt-3">
                <p class="mb-2 text-strong">AI Model :</p>
                <p class="font-size-16 mb-2 justify-content-between">
                  <span class="mw-80">{{questionDetails.ai_model}}</span>
                </p>
              </div>

              <hr />

              <b-button
                  class="mb-2 d-block m-auto mwb-11"
                  :variant="questionDetails.is_approved ? 'warning' : 'primary'"
                  :title="questionDetails.is_approved ? 'Unapprove Question' : 'Approve Question'"
                  :disabled="loadingStates.approving || loadingStates.rejecting"
                  @click="toggleApproveQuestion()"
              >
                <b-spinner small v-if="loadingStates.approving" class="me-1"></b-spinner>
                <i v-else :class="questionDetails.is_approved ? 'uil-stop' : 'uil-check'"></i>
                {{ loadingStates.approving ? 'Processing...' : (questionDetails.is_approved ? 'Unapprove Question' : 'Approve Question') }}
              </b-button>

              <b-button
                  class="mb-2 d-block m-auto mwb-11"
                  :variant="questionDetails.is_rejected ? 'secondary' : 'danger'"
                  :title="questionDetails.is_rejected ? 'Unreject Question' : 'Reject Question'"
                  :disabled="loadingStates.approving || loadingStates.rejecting"
                  @click="toggleRejectQuestion()"
              >
                <b-spinner small v-if="loadingStates.rejecting" class="me-1"></b-spinner>
                <i v-else :class="questionDetails.is_rejected ? 'uil-check' : 'uil-times'"></i>
                {{ loadingStates.rejecting ? 'Processing...' : (questionDetails.is_rejected ? 'Unreject Question' : 'Reject Question') }}
              </b-button>

              <div class="mt-5">
                <p class="mb-1">
                  Timestamp : <span class="mw-80" v-if="questionDetails.timestamp">{{formatDate(questionDetails.timestamp)}}</span>
                </p>
              </div>
            </div>
          </div>
          <div class="card-body d-flex align-items-center justify-content-center" v-else>
            <b-spinner large></b-spinner>
          </div>
        </div>
      </div>

      <!-- NAV TABS -->
      <div :class="!toggleProfile ? 'col-xl-8' : 'col-xl-10'">
        <!-- Question Details CARD -->
        <div class="card mb-3">
          <b-tabs content-class="p-4" justified class="nav-tabs-custom">

            <b-tab active>
              <template v-slot:title>
                <i class="uil uil-list-ui-alt font-size-20"></i>
                <span class="d-none d-sm-block">Question Answers</span>
              </template>

              <table class="table table-condensed" v-if="!showLoader">
                <thead>
                <tr>
                  <th>Answer</th>
                  <th>Is Correct</th>
                  <th>Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="answer in questionDetails.answers" :key="answer.id">
                  <td>{{ answer.answer }}</td>
                  <td>
                    <b-form-radio
                        :id="'correct-' + answer.id"
                        v-model="selectedCorrectAnswerId"
                        :value="answer.id"
                        @click.native.prevent="handleCorrectAnswerChange(answer.id, $event)"
                    ></b-form-radio>
                  </td>
                  <td>
                    <a href="javascript:void(0);" 
                       class="text-primary" 
                       @click="editAnswer(answer)"
                       title="Edit Answer">
                      <i class="uil uil-pen font-size-18"></i>
                    </a>
                  </td>
                </tr>
                </tbody>
              </table>

               <template v-else>
                   <div class="card-body d-flex align-items-center justify-content-center">
                       <b-spinner large></b-spinner>
                   </div>
               </template>
            </b-tab>


            <b-tab>
              <template v-slot:title>

                <i class="fa fa-language font-size-20 mt-1"></i>

                <span class="d-none d-sm-block">Question Translations</span>
              </template>

              <div v-if="!showLoader">
                <div class="mb-3 text-end">
                  <b-button
                      variant="primary"
                      @click="openTranslateModal()"
                  >
                    <i class="fa fa-language me-1"></i>
                    Translate Question & Answers
                  </b-button>
                </div>
                <div v-for="translation in questionDetails.translations" :key="translation.id" class="mb-4">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h5 class="mb-0">
                        Language - {{ translation.language.toUpperCase() }}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="d-flex">
                        <p class="mb-3 me-3"><strong>Question:</strong> {{ translation.question }}</p>
                        <a href="javascript:void(0);"
                           @click="changeQuestionTranslation(translation.language)"
                           :title="'Edit ' + translation.language.toUpperCase() + ' Question Translation'"
                           class="text-primary ms-2">
                          <i class="uil uil-pen font-size-16"></i>
                        </a>
                      </div>
                      
                      <hr />
                      
                      <h6 class="mb-3"><strong>Answers:</strong></h6>
                      <table class="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th style="width: 60%">Answer</th>
                            <th style="width: 30%">Default (EN)</th>
                            <th style="width: 10%">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="answer in questionDetails.answers" :key="answer.id">
                            <td>
                              {{ getAnswerTranslation(answer, translation.language) }}
                            </td>
                            <td class="text-muted">
                              {{ answer.answer }}
                            </td>
                            <td>
                              <a href="javascript:void(0);"
                                 @click="changeAnswerTranslation(answer.id, translation.language, answer.answer)"
                                 :title="'Edit ' + translation.language.toUpperCase() + ' Answer Translation'"
                                 class="text-primary">
                                <i class="uil uil-pen font-size-16"></i>
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <template v-else>
                <div class="card-body d-flex align-items-center justify-content-center">
                  <b-spinner large></b-spinner>
                </div>
              </template>
            </b-tab>

            <!-- Review Images Tab -->
            <b-tab>
              <template v-slot:title>
                <i class="uil uil-image font-size-20 mt-1"></i>
                <span class="d-none d-sm-block">Review Images</span>
              </template>

              <div v-if="!showLoaderImages">
                <!-- Change Image Section -->
                <div class="mb-4">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h5 class="mb-0">Upload New Image</h5>
                    </div>
                    <div class="card-body">
                      <div v-if="!showImageDropzone">
                        <b-button variant="primary" @click="showImageDropzone = true">
                          <i class="bx bx-upload me-1"></i> Upload Image
                        </b-button>
                      </div>

                      <div v-else>
                        <vue-dropzone
                            id="image_url"
                            ref="myVueDropzone_image_url"
                            :use-custom-slot="true"
                            :options="dropzoneOptions_primary"
                            @vdropzone-files-added="selectFileImageUrl"
                            no-progress-bar
                        >
                          <div class="dropzone-custom-content">
                            <i class="display-4 text-muted bx bxs-cloud-upload"></i>
                            <h4>Drop file here or click to upload.</h4>
                          </div>
                        </vue-dropzone>

                        <b-button variant="secondary" class="mt-2" @click="cancelImageUpload" :disabled="uploading">
                          <i class="bx bx-x me-1"></i> Cancel
                        </b-button>
                        <b-button variant="primary" class="mt-2 ms-2" :disabled="!image_url || tryingToEdit || uploading" @click="addImageToReview">
                          <template v-if="uploading">
                            <b-spinner small class="me-1"></b-spinner>
                            Uploading...
                          </template>
                          <template v-else>
                            Upload Image
                          </template>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Reviewed Images List -->
                <div v-if="reviewedImages.length > 0">
                  <div class="table-responsive">
                    <table class="table table-bordered">
                      <thead>
                        <tr>
                          <th>Image</th>
                          <th>Reviewer Name</th>
                          <th>Reviewer Type</th>
                          <th>Status</th>
                          <th>Timestamp</th>
                          <th style="width: 150px;">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(review, index) in reviewedImages" :key="index" :class="{'table-success': isCurrentImage(review.image)}">
                          <td>
                            <div class="position-relative">
                              <a v-if="review.image" :href="review.image" target="_blank">
                                <img 
                                  :src="review.image" 
                                  alt="Review Image" 
                                  class="img-thumbnail"
                                  style="max-width: 150px; max-height: 150px; cursor: pointer;"
                                />
                              </a>
                              <span v-else class="text-muted">No image</span>
                              <span v-if="isCurrentImage(review.image)" class="badge bg-success position-absolute top-0 start-0 m-1">
                                <i class="uil uil-check me-1"></i>In Use
                              </span>
                            </div>
                          </td>
                          <td>{{ review.reviewer_name || 'N/A' }}</td>
                          <td>
                            <span 
                              :class="{
                                'badge bg-primary': review.reviewer_type === 'human',
                                'badge bg-info': review.reviewer_type === 'ai',
                                'badge bg-secondary': !review.reviewer_type
                              }"
                            >
                              {{ review.reviewer_type || 'N/A' }}
                            </span>
                          </td>
                          <td>
                            <span 
                              v-if="isCurrentImage(review.image)"
                              class="badge bg-success"
                            >
                              <i class="uil uil-check-circle me-1"></i>Approved
                            </span>
                            <span 
                              v-else
                              class="badge bg-warning text-dark"
                            >
                              <i class="uil uil-minus-circle me-1"></i>Not Used
                            </span>
                          </td>
                          <td>{{ review.timestamp ? formatDate(review.timestamp) : 'N/A' }}</td>
                          <td>
                            <b-button
                              v-if="!isCurrentImage(review.image)"
                              variant="success"
                              size="sm"
                              @click="approveReviewedImage(review)"
                              :disabled="!review.image"
                              title="Approve & Use This Image"
                            >
                              <i class="uil uil-check me-1"></i>
                              Approve & Use
                            </b-button>
                            <span v-else class="badge bg-success">
                              <i class="uil uil-check-circle me-1"></i>Currently Active
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-else class="text-center py-5">
                  <i class="uil uil-image-slash font-size-48 text-muted"></i>
                  <p class="text-muted mt-3">No reviewed images found for this question.</p>
                </div>
              </div>

              <template v-else>
                <div class="card-body d-flex align-items-center justify-content-center">
                  <b-spinner large></b-spinner>
                </div>
              </template>
            </b-tab>

            <!-- Review Questions Tab -->
            <b-tab>
              <template v-slot:title>
                <i class="uil uil-clipboard-alt font-size-20 mt-1"></i>
                <span class="d-none d-sm-block">Review Questions</span>
              </template>

              <div v-if="!showLoader">
                <div v-if="reviewedQuestions.length > 0">
                  <div :class="['alert mb-4', currentReviewAlertClass]">
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div>
                        <i class="uil uil-info-circle me-2"></i>
                        <strong>Total Reviews:</strong> {{ reviewStats.total }}
                        <span class="ms-3">
                          <strong>Correct:</strong> {{ reviewStats.numCorrect }}
                          <span class="text-muted">({{ reviewStats.percentCorrect }}%)</span>
                          <strong class="ms-3">Incorrect:</strong> {{ reviewStats.numIncorrect }}
                        </span>
                      </div>
                      <div v-if="!isFinalized && reviewRecommendation">
                        <small class="text-muted me-2">Suggested action:</small>
                        <b-button
                          size="sm"
                          :variant="reviewRecommendation === 'approve' ? 'success' : 'danger'"
                          :disabled="loadingStates.approving || loadingStates.rejecting"
                          @click="reviewRecommendation === 'approve' ? toggleApproveQuestion() : toggleRejectQuestion()"
                        >
                          <b-spinner small v-if="loadingStates.approving || loadingStates.rejecting" class="me-1"></b-spinner>
                          <template v-else>
                            {{ reviewActionLabel }}
                          </template>
                        </b-button>
                      </div>
                      <div v-else>
                        <span class="fw-semibold">{{ finalStatusText }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered table-striped">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 5%">#</th>
                          <th style="width: 15%">Reviewer Name</th>
                          <th style="width: 12%">Reviewer Type</th>
                          <th style="width: 10%">Review Language</th>
                          <th style="width: 12%">Is Correct</th>
                          <th style="width: 30%">Note</th>
                          <th style="width: 16%">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(review, index) in reviewedQuestions" :key="review.id">
                          <td>{{ index + 1 }}</td>
                          <td>
                            <i class="uil uil-user me-1"></i>
                            {{ review.reviewer_name || 'N/A' }}
                          </td>
                          <td>
                            <span 
                              :class="{
                                'badge bg-primary': review.reviewer_type === 'human',
                                'badge bg-info': review.reviewer_type === 'ai',
                                'badge bg-secondary': !review.reviewer_type
                              }"
                            >
                              {{ review.reviewer_type || 'N/A' }}
                            </span>
                          </td>
                          <td>
                            <span class="badge bg-light text-dark">
                              {{ review.language ? review.language.toUpperCase() : 'N/A' }}
                            </span>
                          </td>
                          <td>
                            <span 
                              v-if="review.is_correct === 1"
                              class="badge bg-success"
                            >
                              <i class="uil uil-check me-1"></i>Correct
                            </span>
                            <span 
                              v-else-if="review.is_correct === 0"
                              class="badge bg-danger"
                            >
                              <i class="uil uil-times me-1"></i>Incorrect
                            </span>
                            <span 
                              v-else-if="review.is_correct === 2"
                              class="badge bg-secondary"
                            >
                              <i class="uil uil-forward me-1"></i>Skipped
                            </span>
                            <span 
                              v-else
                              class="badge bg-warning text-dark"
                            >
                              <i class="uil uil-question-circle me-1"></i>Unknown
                            </span>
                          </td>
                          <td>
                            <div v-if="review.note" class="text-wrap">
                              {{ review.note }}
                            </div>
                            <span v-else class="text-muted fst-italic">No notes</span>
                          </td>
                          <td>
                            <small>{{ review.timestamp ? formatDate(review.timestamp) : 'N/A' }}</small>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-else class="text-center py-5">
                  <i class="uil uil-clipboard-notes font-size-48 text-muted"></i>
                  <p class="text-muted mt-3">No reviews found for this question.</p>
                </div>
              </div>

              <template v-else>
                <div class="card-body d-flex align-items-center justify-content-center">
                  <b-spinner large></b-spinner>
                </div>
              </template>
            </b-tab>

          </b-tabs>
        </div>


        <!--                <div class="card mb-3">
                            <b-tabs content-class="p-4" justified class="nav-tabs-custom">

                                <b-tab active>
                                   &lt;!&ndash; <template v-slot:title>
                                        <i class="uil uil-list-ui-alt font-size-20"></i>
                                        <span class="d-none d-sm-block">Matches - {{ today }}</span>
                                    </template>&ndash;&gt;


                                </b-tab>

                            </b-tabs>
                        </div>-->



      </div>
    </div>
    <div v-else class="text-center mt-5">
      <div class="row">
        <div class="col-md-12">
          <h4>{{this.error}}</h4>
        </div>
      </div>
    </div>
    <!-- end row -->

    <!-- MODALS -->
    <modalTranslateQuestion 
      ref="translateQuestionModal" 
      :questionData="questionDetails"
      @onRefresh="getQuestionDetails"
    />
    <!-- END  MODALS -->
  </Layout>
</template>
